import { Icon } from '@scality/core-ui';
import type React from 'react';
import { useRef, useState } from 'react';
import type { Panel } from '../types/panel';
import type { PanelId } from '../types/panelPlacement';
import { PanelTab, PanelTabBar, PanelTabClose, PanelTabInput, PanelTabLabel } from './dualPanel.styles';

type PanelTabsProps = {
  panels: Panel[];
  activePanelId: PanelId | null;
  onSelect: (id: PanelId) => void;
  onRename: (id: PanelId, title: string) => void;
  /** When provided, closable panels show a close button. The dashboard panel is never closable. */
  onClose?: (id: PanelId) => void;
};

export const PanelTabs: React.FC<PanelTabsProps> = ({ panels, activePanelId, onSelect, onRename, onClose }) => {
  const [editingId, setEditingId] = useState<PanelId | null>(null);
  const [draft, setDraft] = useState('');

  const settledRef = useRef(false);

  const startEditing = (panel: Panel) => {
    settledRef.current = false;
    setEditingId(panel.id);
    setDraft(panel.title);
  };

  const finishEditing = (save: boolean) => {
    if (settledRef.current) {
      return;
    }
    settledRef.current = true;
    if (save && editingId !== null) {
      onRename(editingId, draft);
    }
    setEditingId(null);
  };

  const handleTabCloseKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>, panelId: PanelId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClose?.(panelId);
    }
  };

  return (
    <PanelTabBar role="tablist">
      {panels.map((panel) => {
        // The dashboard panel is pinned; only widget panels can be closed.
        const isClosable = onClose != null && panel.source !== 'dashboard';
        return (
          <PanelTab
            key={panel.id}
            type="button"
            role="tab"
            aria-selected={panel.id === activePanelId}
            $active={panel.id === activePanelId}
            onClick={() => onSelect(panel.id)}
            onDoubleClick={() => startEditing(panel)}
          >
            {editingId === panel.id ? (
              <PanelTabInput
                value={draft}
                autoFocus
                aria-label={`Rename ${panel.title}`}
                onChange={(event) => setDraft(event.target.value)}
                onClick={(event) => event.stopPropagation()}
                onFocus={(event) => event.target.select()}
                onBlur={() => finishEditing(true)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    finishEditing(true);
                  } else if (event.key === 'Escape') {
                    finishEditing(false);
                  }
                }}
              />
            ) : (
              <PanelTabLabel>{panel.title}</PanelTabLabel>
            )}
            {isClosable && (
              <PanelTabClose
                role="button"
                aria-label={`Close ${panel.title}`}
                tabIndex={0}
                onKeyDown={(event) => handleTabCloseKeyDown(event, panel.id)}
                onClick={(event) => {
                  event.stopPropagation();
                  onClose?.(panel.id);
                }}
              >
                <Icon name="Close" size="xs" />
              </PanelTabClose>
            )}
          </PanelTab>
        );
      })}
    </PanelTabBar>
  );
};
